import { useEffect, useMemo, useState } from "react";
import { Check, FileText, HandCoins, Plus, Search, Trash2 } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import { ErrorConsulta, EsqueletoLista } from "../componentes/Errores.tsx";
import type { Abono, CampoEntidad, FacturaCartera, ProblemaCampo } from "../api/tipos.ts";
import {
  usarAbonos,
  usarBorrarAbono,
  usarBorrarFactura,
  usarCampos,
  usarCrearAbono,
  usarCrearFactura,
  usarFacturasCartera,
} from "../ganchos/datos.ts";
import { PERMISO } from "../sesion/permisos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { fechaCorta, pesos } from "../formato.ts";
import { coincideNumero, motivoNoCruza } from "../cruce.ts";
import { Aviso, Boton, Campo, Dialogo, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";

type Entidad = "abonos" | "facturas";

/**
 * Cómo se le llama a cada campo en pantalla (SPEC-029 §9). El servidor los
 * describe con el nombre de la columna de la hoja —«abono/pago», «metodo»,
 * «codigo»—, que es el contrato y no cambia; quien registra un pago a mano
 * necesita otra cosa.
 */
const ROTULO_CAMPO: Record<string, string> = {
  fechaPago: "Fecha del pago",
  cliente: "Cliente",
  monto: "Monto",
  metodo: "Medio de pago",
  nit: "Documento (NIT o cédula)",
  empresa: "Empresa",
  factura: "Factura",
  novedad: "Observación",
  codigo: "Código en tu sistema",
};
const rotuloDe = (c: { campo: string; etiqueta: string }) => ROTULO_CAMPO[c.campo] ?? c.etiqueta;

/**
 * Registro de cartera.
 *
 * El motor cruza tres cosas y solo una llega sola: los MOVIMIENTOS, por el
 * correo del banco. Las otras dos entran por aquí — la FACTURA, que es un hecho
 * de la DIAN, y el ABONO, que es una afirmación humana: alguien dice "este pago
 * es de tal cliente". Sin ellas el sistema ve entrar plata y no sabe de quién es.
 *
 * Los campos del formulario NO están escritos aquí: los sirve el backend desde
 * el mismo esquema con el que valida. Copiarlos habría garantizado que un día
 * este formulario pidiera un campo que ya no existe, o dejara de pedir uno
 * obligatorio y el usuario lo descubriera al guardar.
 */
export function Registrar() {
  const { puedo } = usarSesion();
  const [entidad, setEntidad] = useState<Entidad>("abonos");
  const puedeCargar = puedo(PERMISO.CARGAR_DATOS);

  return (
    <Seccion
      titulo="Registrar"
      descripcion={
        puedeCargar
          ? "Los pagos que recibiste y las facturas que emitiste. Es contra esto que el sistema cruza lo que entra al banco."
          : "Los pagos y facturas registrados. Cargarlos es cosa del contador o del dueño."
      }
    >
      <div className="flex gap-1 rounded-xl border border-borde bg-superficie-2 p-1">
        {(
          [
            ["abonos", "Pagos registrados", HandCoins],
            ["facturas", "Facturas", FileText],
          ] as const
        ).map(([clave, texto, Icono]) => (
          <button
            key={clave}
            type="button"
            onClick={() => setEntidad(clave)}
            aria-pressed={entidad === clave}
            className={cx(
              "flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              "focus-visible:ring-4 focus-visible:ring-marca/12 focus-visible:outline-none",
              entidad === clave
                ? "bg-superficie text-tinta shadow-sm"
                : "text-tenue hover:text-tinta",
            )}
          >
            <Icono className="size-4" aria-hidden />
            {texto}
          </button>
        ))}
      </div>

      {entidad === "abonos" ? <PanelAbonos puedeCargar={puedeCargar} /> : <PanelFacturas puedeCargar={puedeCargar} />}
    </Seccion>
  );
}

// ─────────────────────────── formulario ───────────────────────────

/** Un abono se captura en formato de hoja colombiana, no en el del banco. */
const AYUDA: Record<string, string> = {
  dinero: "Como en tu hoja: 140.000 o 1.698.999,99",
};

function tipoDeInput(campo: CampoEntidad) {
  if (campo.tipo === "fecha") return "date";
  // Dinero va como texto, no como number: el navegador rechazaría "140.000",
  // que es exactamente como lo escribe alguien en Colombia.
  return "text";
}

interface PropsPersonalizado {
  valor: string;
  alCambiar: (v: string) => void;
  error?: string;
  /** El resto del formulario, para campos que dependen de otro (la factura, del NIT). */
  valores: Record<string, string>;
}

function Formulario({
  campos,
  onGuardar,
  guardando,
  exitos,
  problemas,
  error,
  etiquetaBoton,
  personalizados = {},
}: {
  campos: CampoEntidad[];
  onGuardar: (datos: Record<string, string>) => void;
  guardando: boolean;
  /**
   * Cuántos guardados han salido bien. Al cambiar, el formulario se vacía.
   *
   * Se pasa un contador y no un booleano porque dos guardados seguidos tienen
   * que limpiar dos veces, y un `true` que ya era `true` no dispara nada.
   */
  exitos: number;
  problemas: ProblemaCampo[];
  error: string | null;
  etiquetaBoton: string;
  personalizados?: Record<string, (p: PropsPersonalizado) => React.ReactNode>;
}) {
  const [valores, setValores] = useState<Record<string, string>>({});

  // Se vacía al guardar bien. Sin esto, los campos se quedaban llenos después
  // de crear el registro: volver a pulsar «Guardar» creaba otro idéntico, y el
  // índice único no lo atrapa porque el panel no manda `codigo` —esa unicidad
  // es parcial a propósito (migración 002-abono-idempotente)—.
  //
  // Un abono duplicado no es cosmético: hace que una factura parezca pagada dos
  // veces, sale de la cartera y nadie lo revisa, porque el sistema no está
  // confundido, está convencido.
  useEffect(() => {
    if (exitos > 0) setValores({});
  }, [exitos]);
  const porCampo = useMemo(
    () => Object.fromEntries(problemas.map((p) => [p.campo, p.detalle])),
    [problemas],
  );

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    onGuardar(valores);
  }

  return (
    <Tarjeta className="space-y-5 p-6">
      <form onSubmit={enviar} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {campos.map((c) => {
            const personalizado = personalizados[c.campo];
            if (personalizado) {
              return (
                <div key={c.campo} className="sm:col-span-2 lg:col-span-1">
                  {personalizado({
                    valor: valores[c.campo] ?? "",
                    alCambiar: (v) => setValores((prev) => ({ ...prev, [c.campo]: v })),
                    error: porCampo[c.campo],
                    valores,
                  })}
                </div>
              );
            }
            return (
              <Campo
                key={c.campo}
                etiqueta={c.obligatorio ? `${rotuloDe(c)} *` : rotuloDe(c)}
                type={tipoDeInput(c)}
                value={valores[c.campo] ?? ""}
                onChange={(e) => setValores((v) => ({ ...v, [c.campo]: e.target.value }))}
                ayuda={AYUDA[c.tipo]}
                error={porCampo[c.campo]}
                inputMode={c.tipo === "dinero" ? "decimal" : undefined}
              />
            );
          })}
        </div>

        {error && !problemas.length ? <Aviso titulo="No se pudo guardar">{error}</Aviso> : null}

        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-tenue">Los campos con * son obligatorios.</p>
          <Boton type="submit" icono={Plus} cargando={guardando}>
            {etiquetaBoton}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  );
}

/** Extrae los problemas por campo de un 422; cualquier otro error no los trae. */
function problemasDe(error: unknown): ProblemaCampo[] {
  if (error instanceof ErrorApi && Array.isArray(error.datos.problemas)) {
    return error.datos.problemas as ProblemaCampo[];
  }
  return [];
}

const mensajeDe = (error: unknown) => (error instanceof Error ? error.message : null);

// ─────────────── buscador de factura, dentro del abono ───────────────

/**
 * El campo "factura" de un abono, con las facturas ya cargadas a mano.
 *
 * Sugiere, pero **no obliga**: un abono es una afirmación humana y puede
 * referirse a una factura que todavía no se ha registrado. Cerrar el campo a
 * una lista rompería el modelo (SPEC-000 §1) y dejaría al contador sin poder
 * anotar lo que sí ocurrió.
 *
 * Lo que sí hace es avisar de un fallo que de otro modo es invisible: si el NIT
 * del abono y el de la factura no coinciden, el motor **nunca** los cruzará
 * —"no forzamos el cruce", dice `buscarFactura`— y el pago quedaría suelto sin
 * que nada lo señale.
 */
function BuscadorFactura({ valor, alCambiar, error, valores }: PropsPersonalizado) {
  const facturas = usarFacturasCartera();
  const [desplegado, setDesplegado] = useState(false);
  const [creando, setCreando] = useState(false);
  const nitAbono = valores.nit ?? "";

  const lista = facturas.data ?? [];

  // Se prioriza lo que sí va a cruzar: primero las del mismo NIT.
  const sugerencias = useMemo(() => {
    const texto = valor.trim().toUpperCase();
    const puntua = (f: FacturaCartera) => {
      const mismoTitular = nitAbono ? !motivoNoCruza(nitAbono, f.nit) : false;
      return mismoTitular ? 0 : 1;
    };
    return lista
      .filter((f) => !texto || f.numero.toUpperCase().includes(texto) || coincideNumero(valor, f.numero))
      .sort((a, b) => puntua(a) - puntua(b))
      .slice(0, 6);
  }, [lista, valor, nitAbono]);

  const elegida = useMemo(
    () => (valor.trim() ? lista.find((f) => coincideNumero(valor, f.numero)) : undefined),
    [lista, valor],
  );
  const aviso = elegida ? motivoNoCruza(nitAbono, elegida.nit) : null;
  const noExiste = Boolean(valor.trim()) && !elegida && !facturas.isLoading;

  return (
    <div className="relative space-y-1.5">
      <Campo
        etiqueta="Factura"
        icono={Search}
        value={valor}
        onChange={(e) => {
          alCambiar(e.target.value);
          setDesplegado(true);
        }}
        onFocus={() => setDesplegado(true)}
        // El cierre se retrasa para que el clic en una sugerencia llegue antes
        // que el blur; si no, la lista desaparece justo al pulsarla.
        onBlur={() => setTimeout(() => setDesplegado(false), 150)}
        placeholder="Busca o escribe el número"
        error={error}
        autoComplete="off"
      />

      {elegida && !aviso ? (
        <p className="flex items-center gap-1.5 text-xs text-exito-tinta">
          <Check className="size-3.5" aria-hidden />
          {elegida.numero} · {pesos(elegida.totalCentavos)}
          {elegida.nit ? ` · NIT ${elegida.nit}` : ""}
        </p>
      ) : null}

      {aviso ? <Aviso tono="aviso" titulo="Esta factura no va a cruzar">{aviso}</Aviso> : null}

      {noExiste ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-borde bg-superficie-2 px-3 py-2">
          <p className="text-xs text-tenue">No hay ninguna factura con ese número.</p>
          <Boton variante="fantasma" icono={Plus} onClick={() => setCreando(true)}>
            Registrarla
          </Boton>
        </div>
      ) : null}

      {desplegado && sugerencias.length ? (
        <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-borde bg-superficie shadow-lg">
          {sugerencias.map((f) => {
            const noCruza = motivoNoCruza(nitAbono, f.nit);
            return (
              <li key={f.id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    alCambiar(f.numero);
                    setDesplegado(false);
                  }}
                  className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-superficie-2"
                >
                  <span className="font-medium text-tinta">{f.numero}</span>
                  <span className={cx("text-xs", noCruza ? "text-aviso-tinta" : "text-tenue")}>
                    {f.nit || "sin NIT"} · {pesos(f.totalCentavos)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <DialogoNuevaFactura
        abierto={creando}
        alCerrar={() => setCreando(false)}
        numero={valor}
        nit={nitAbono}
        alCrear={(numero) => {
          alCambiar(numero);
          setCreando(false);
        }}
      />
    </div>
  );
}

/** Registrar una factura sin abandonar el abono que se estaba escribiendo. */
function DialogoNuevaFactura({
  abierto,
  alCerrar,
  numero,
  nit,
  alCrear,
}: {
  abierto: boolean;
  alCerrar: () => void;
  numero: string;
  nit: string;
  alCrear: (numero: string) => void;
}) {
  const crear = usarCrearFactura();
  const [total, setTotal] = useState("");
  const [nitFactura, setNitFactura] = useState(nit);
  const [vence, setVence] = useState("");

  // El NIT del abono se hereda: es el que hará que las dos casen. Se puede
  // cambiar, pero el valor por defecto es el que funciona.
  useEffect(() => {
    if (abierto) setNitFactura(nit);
  }, [abierto, nit]);

  function guardar(e: React.FormEvent) {
    e.preventDefault();
    crear.mutate(
      { factura: numero, nit: nitFactura, total, vence: vence || undefined },
      { onSuccess: () => alCrear(numero) },
    );
  }

  const problemas = problemasDe(crear.error);
  const porCampo = Object.fromEntries(problemas.map((p) => [p.campo, p.detalle]));

  return (
    <Dialogo
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={`Registrar la factura ${numero}`}
      descripcion="Se crea con el documento del pago, que es lo que hace que los dos se crucen."
      pie={
        <>
          <Boton variante="fantasma" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" onClick={guardar} cargando={crear.isPending}>
            Registrar factura
          </Boton>
        </>
      }
    >
      <form onSubmit={guardar} className="space-y-4">
        <Campo etiqueta="NIT *" value={nitFactura} onChange={(e) => setNitFactura(e.target.value)} error={porCampo.nit} />
        <Campo
          etiqueta="Total *"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          ayuda={AYUDA.dinero}
          inputMode="decimal"
          error={porCampo.total}
        />
        <Campo etiqueta="Vence" type="date" value={vence} onChange={(e) => setVence(e.target.value)} error={porCampo.vence} />
        {crear.error && !problemas.length ? (
          <Aviso titulo="No se pudo registrar">{mensajeDe(crear.error)}</Aviso>
        ) : null}
      </form>
    </Dialogo>
  );
}

// ─────────────────────────── abonos ───────────────────────────

function PanelAbonos({ puedeCargar }: { puedeCargar: boolean }) {
  const campos = usarCampos();
  const abonos = usarAbonos();
  const crear = usarCrearAbono();
  const borrar = usarBorrarAbono();
  const [guardados, setGuardados] = useState(0);
  const contarGuardado = () => setGuardados((n) => n + 1);


  if (abonos.isError) return <ErrorConsulta error={abonos.error} alReintentar={() => void abonos.refetch()} />;

  return (
    <div className="space-y-6">
      {puedeCargar && campos.data ? (
        <Formulario
          campos={campos.data.abonos}
          onGuardar={(datos) => crear.mutate(datos, { onSuccess: contarGuardado })}
          guardando={crear.isPending}
          exitos={guardados}
          problemas={problemasDe(crear.error)}
          error={mensajeDe(crear.error)}
          etiquetaBoton="Registrar pago"
          personalizados={{ factura: (props) => <BuscadorFactura {...props} /> }}
        />
      ) : null}

      {abonos.isLoading ? (
        <EsqueletoLista />
      ) : !abonos.data?.length ? (
        <Vacio icono={HandCoins} titulo="Todavía no hay pagos registrados">
          Un pago registrado es tu anotación de quién pagó y por qué factura. Es lo que 4drando
          contrasta con lo que entra al banco.
        </Vacio>
      ) : (
        <Tarjeta className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-borde text-left text-xs text-tenue">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Factura</th>
                <th className="px-4 py-3 font-medium">Método</th>
                <th className="px-4 py-3 text-right font-medium">Monto</th>
                {puedeCargar && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {abonos.data.map((a: Abono) => (
                <tr key={a.id} className="border-b border-borde/60 last:border-0">
                  <td className="px-4 py-3 whitespace-nowrap text-tenue">{fechaCorta(a.fechaPago)}</td>
                  <td className="px-4 py-3 font-medium text-tinta">{a.cliente}</td>
                  <td className="px-4 py-3 text-tenue">{a.factura || "—"}</td>
                  <td className="px-4 py-3 text-tenue">{a.metodo}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-tinta">{pesos(a.montoCentavos)}</td>
                  {puedeCargar && (
                    <td className="px-4 py-3 text-right">
                      <Boton
                        variante="fantasma"
                        icono={Trash2}
                        aria-label={`Borrar el pago de ${a.cliente}`}
                        cargando={borrar.isPending && borrar.variables === a.id}
                        onClick={() => borrar.mutate(a.id)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Tarjeta>
      )}
    </div>
  );
}

// ─────────────────────────── facturas ───────────────────────────

function PanelFacturas({ puedeCargar }: { puedeCargar: boolean }) {
  const campos = usarCampos();
  const facturas = usarFacturasCartera();
  const crear = usarCrearFactura();
  const borrar = usarBorrarFactura();
  const [guardados, setGuardados] = useState(0);
  const contarGuardado = () => setGuardados((n) => n + 1);

  if (facturas.isError)
    return <ErrorConsulta error={facturas.error} alReintentar={() => void facturas.refetch()} />;

  return (
    <div className="space-y-6">
      {puedeCargar && campos.data ? (
        <>
          {/* El número de factura es la identidad: repetirlo actualiza la que había. */}
          <Aviso tono="dato" titulo="Sobre el número de factura">
            Si registras un número que ya existe, se actualiza esa factura en vez de crear otra.
          </Aviso>
          <Formulario
            campos={campos.data.facturas}
            onGuardar={(datos) => crear.mutate(datos, { onSuccess: contarGuardado })}
            guardando={crear.isPending}
            exitos={guardados}
            problemas={problemasDe(crear.error)}
            error={mensajeDe(crear.error)}
            etiquetaBoton="Registrar factura"
          />
        </>
      ) : null}

      {facturas.isLoading ? (
        <EsqueletoLista />
      ) : !facturas.data?.length ? (
        <Vacio icono={FileText} titulo="Todavía no hay facturas">
          Sin facturas el sistema puede ver entrar un pago, pero no contra qué saldarlo.
        </Vacio>
      ) : (
        <Tarjeta className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-borde text-left text-xs text-tenue">
                <th className="px-4 py-3 font-medium">Factura</th>
                <th className="px-4 py-3 font-medium">NIT</th>
                <th className="px-4 py-3 font-medium">Emisión</th>
                <th className="px-4 py-3 font-medium">Vence</th>
                <th className="px-4 py-3 text-right font-medium">Total</th>
                {puedeCargar && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {facturas.data.map((f: FacturaCartera) => (
                <tr key={f.id} className="border-b border-borde/60 last:border-0">
                  <td className="px-4 py-3 font-medium text-tinta">{f.numero}</td>
                  <td className="px-4 py-3 tabular-nums text-tenue">{f.nit || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-tenue">{fechaCorta(f.emision)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-tenue">{fechaCorta(f.vence)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-tinta">{pesos(f.totalCentavos)}</td>
                  {puedeCargar && (
                    <td className="px-4 py-3 text-right">
                      <Boton
                        variante="fantasma"
                        icono={Trash2}
                        aria-label={`Borrar la factura ${f.numero}`}
                        cargando={borrar.isPending && borrar.variables === f.id}
                        onClick={() => borrar.mutate(f.id)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Tarjeta>
      )}
    </div>
  );
}
